import { settingRowStyle as rowStyle, settingLabelColumnStyle as labelColumnStyle, settingLabelStyle as labelStyle, settingsDescriptionStyle as descriptionStyle, settingOrgIndicatorStyle as orgIndicatorStyle, settingsInputStyle } from './settingsStyles';
// Phase 7.2 — Select row for settings entries with controlType='select'.
//
// Renders a label, optional org-lock indicator, description, and a
// <select> element. Matches the visual pattern of SettingToggleRow.

import type { CSSProperties, ReactElement } from 'react';
import type { SettingsSchemaEntry } from '@/lib/settings-schema';

const selectColumnStyle: CSSProperties = {
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
};

export interface SettingSelectRowProps {
  readonly entry: SettingsSchemaEntry;
  readonly value: string;
  readonly showOrgLock: boolean;
  readonly disabled: boolean;
  readonly disabledReason?: string;
  readonly onChange: (next: string) => void;
}

export function SettingSelectRow({
  entry,
  value,
  showOrgLock,
  disabled,
  disabledReason,
  onChange,
}: SettingSelectRowProps): ReactElement {
  const currentOption = entry.options?.find((o) => o.value === value);
  return (
    <div
      style={rowStyle}
      data-testid={`setting-row-${entry.key}`}
      data-setting-level={entry.level}
    >
      <div style={labelColumnStyle}>
        <div style={labelStyle}>
          {showOrgLock && (
            <span
              aria-label="Organization compliance control"
              title="Organization compliance control"
            >
              🔒
            </span>
          )}
          <span>{entry.label}</span>
        </div>
        <div style={descriptionStyle}>{entry.description}</div>
        {currentOption?.description && (
          <div style={{ ...descriptionStyle, marginTop: 2, fontStyle: 'italic' }}>
            {currentOption.description}
          </div>
        )}
        {showOrgLock && (
          <div style={orgIndicatorStyle}>Organization compliance control</div>
        )}
      </div>
      <div style={selectColumnStyle}>
        <select
          data-testid={`setting-select-${entry.key}`}
          value={value}
          disabled={disabled}
          title={disabled ? disabledReason : undefined}
          aria-label={entry.label}
          onChange={(e) => onChange(e.target.value)}
          style={{
            ...settingsInputStyle,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            minWidth: 140,
          }}
        >
          {(entry.options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
